import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { invoiceKeys, jobKeys, recurringBillingKeys } from '@/lib/queryKeys';
import { recurringBillingEndpoints } from '@/lib/api/endpoints';
import { apiPost, operationsClient } from '@/lib/api/client';
import { failureTitle } from '@/lib/recurring-billing/viewModel';
import type { RecurringBillingRunSummary } from '@/types/recurring-billing';

/** The run that billed the visit, as the server settled it. */
type BilledVisitRun = Pick<RecurringBillingRunSummary, 'status' | 'errorCode'> | null;

/**
 * Bill a completed visit of a per-visit series that no live invoice covers:
 * its invoice was voided (nothing re-bills it on its own — a void can be a
 * waiver), or no run ever billed it.
 */
export function useBillVisitAgain(jobId: string) {
  const queryClient = useQueryClient();

  return useApiMutation<BilledVisitRun, string>({
    mutationFn: (appointmentId) =>
      apiPost<BilledVisitRun>(
        operationsClient,
        recurringBillingEndpoints.billVisitAgain(appointmentId),
        {},
      ),
    onSuccess: (run) => {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.byJobPrefix(jobId) });
      void queryClient.invalidateQueries({ queryKey: jobKeys.financialSummary(jobId) });
      void queryClient.invalidateQueries({ queryKey: recurringBillingKeys.all() });
      // Said "Visit billed" of every run, one that billed nothing included.
      if (run?.status === 'SKIPPED') {
        toast.info('Nothing to bill on this visit');
      } else if (run?.status === 'FAILED') {
        toast.error(failureTitle(run.errorCode));
      } else {
        toast.success('Visit billed');
      }
    },
  });
}
